import * as React from 'react';
import { useMyAgentConfigsQuery } from '../../../queries/Support/useSupportAgentConfig';
import { ISupportMailbox, useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';

/**
 * Mailboxes the user can create tickets in. A role claim makes a mailbox visible but not writable —
 * the API resolves `SupportAccess.Write` to agent membership — so the rest are dead ends.
 */
export function useAgentMailboxes(): { mailboxes: ISupportMailbox[]; isLoading: boolean; isError: boolean } {
	const { data, isLoading: mailboxesLoading, isError: mailboxesFailed } = useSupportMailboxListQuery();
	const mailboxList = React.useMemo(() => data ?? [], [data]);
	const mailboxIds = React.useMemo(() => mailboxList.map(mailbox => mailbox.id), [mailboxList]);

	const { isLoading: configsLoading, isError: configsFailed, configs } = useMyAgentConfigsQuery(mailboxIds);

	const mailboxes = React.useMemo(() => {
		const agentMailboxIds = new Set(configs.filter(config => config.data?.isAgent).map(config => config.mailboxId));
		return mailboxList.filter(mailbox => agentMailboxIds.has(mailbox.id));
	}, [configs, mailboxList]);

	// A failed fetch is not an empty result: both queries settle with isLoading false and no data, and
	// callers must not read that as "this user is an agent nowhere".
	return { mailboxes, isLoading: mailboxesLoading || configsLoading, isError: mailboxesFailed || configsFailed };
}
